import React, { useEffect, useState } from "react";
import { apiRequest } from "../api";
import { fetchAccess } from "../access";
import HeadquartersPage from "../components/headquarters/HeadquartersPage";

const REPORTS = [
  ["estimates", "Estimates"],
  ["jobs", "Jobs"],
  ["invoices", "Invoices"],
  ["overhead", "Monthly costs"],
  ["leads", "Leads"],
  ["lead-sources", "Lead sources"],
  ["customers", "Customers"],
  ["hours", "Software hours"],
  ["personal", "Personal bills"],
  ["pnl", "Profit and loss"],
  ["aging", "A/R aging"],
  ["sales-customer", "Sales by customer"],
  ["sales-item", "Sales by item"],
  ["expenses", "Expenses"],
  ["balances", "Balances"],
];

const FORMS = {
  overhead: {
    report_key: "overhead",
    fields: [
      ["month", "Month", "month"],
      ["what", "What it is", "text"],
      ["monthly_amount", "Monthly amount", "number"],
      ["paid_from", "Paid from", "text"],
    ],
  },
  personal: {
    report_key: "personal",
    fields: [
      ["bill", "Bill", "text"],
      ["amount", "Amount", "number"],
      ["due_day", "Due day", "text"],
      ["status", "Status", "text"],
      ["apr", "APR", "text"],
      ["balance", "Balance owed", "number"],
    ],
  },
  hours: {
    report_key: "hours",
    fields: [
      ["week_of", "Week of", "date"],
      ["build_hours", "Building or fixing", "number"],
      ["sell_hours", "Selling", "number"],
      ["support_hours", "Supporting users", "number"],
    ],
  },
  leads: {
    report_key: "lead",
    fields: [
      ["date_received", "Date received", "date"],
      ["source", "Source", "text"],
      ["name", "Name", "text"],
      ["price_paid", "Price paid", "number"],
      ["job_type", "Job type", "text"],
      ["city", "City", "text"],
      ["contacted", "Contacted", "text"],
      ["went_out", "Went out (hours and miles)", "text"],
      ["bid_amount", "Bid amount", "number"],
      ["outcome", "Outcome", "text"],
      ["contract_amount", "Contract amount if won", "number"],
    ],
  },
  estimates: {
    report_key: "estimate_extra",
    ref_type: "estimate",
    fields: [
      ["ref_id", "Estimate id", "number"],
      ["job_type", "Job type", "text"],
      ["city", "City", "text"],
      ["estimating_hours", "Estimating hours", "number"],
    ],
  },
  jobs: {
    report_key: "job_extra",
    ref_type: "job",
    fields: [
      ["ref_id", "Job id", "number"],
      ["city", "City", "text"],
      ["start_date", "Start date", "date"],
      ["finish_date", "Finish date", "date"],
      ["change_orders", "Change orders", "number"],
      ["labor_hours", "Your labor hours", "number"],
      ["miles", "Round-trip miles", "number"],
    ],
  },
};

function csvCell(value) {
  const text = value == null ? "" : String(value);
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function downloadCsv(report) {
  const columns = report.columns.filter((column) => column.key !== "entry_id");
  const lines = [columns.map((column) => csvCell(column.label)).join(",")];
  report.rows.forEach((row) => {
    lines.push(columns.map((column) => csvCell(row[column.key])).join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${report.key}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function Books({ embedded = false }) {
  const [access, setAccess] = useState(null);
  const [key, setKey] = useState("jobs");
  const [days, setDays] = useState("0");
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState({});

  async function load(nextKey = key, nextDays = days) {
    setErr("");
    const data = await apiRequest(`/reports/books/${nextKey}?days=${nextDays}`);
    setReport(data);
  }

  useEffect(() => {
    fetchAccess().then((next) => {
      setAccess(next);
      if (next.access_level === "owner") {
        load("jobs", "0").catch((error) => setErr(error.message || "Could not load the report"));
      }
    });
  }, []);

  async function saveEntry(event) {
    event.preventDefault();
    const form = FORMS[key];
    if (!form) return;
    setMsg("");
    setErr("");
    const payload = {};
    form.fields.forEach(([name]) => {
      if (name !== "ref_id" && draft[name] !== undefined && draft[name] !== "") payload[name] = draft[name];
    });
    try {
      await apiRequest("/reports/books/entries", {
        method: "POST",
        body: {
          report_key: form.report_key,
          ref_type: form.ref_type || null,
          ref_id: draft.ref_id ? Number(draft.ref_id) : null,
          payload,
        },
      });
      setDraft({});
      setMsg("Saved.");
      await load();
    } catch (error) {
      setErr(error.message || "Could not save");
    }
  }

  async function removeEntry(entryId) {
    setErr("");
    try {
      await apiRequest(`/reports/books/entries/${entryId}`, { method: "DELETE" });
      await load();
    } catch (error) {
      setErr(error.message || "Could not remove that row");
    }
  }

  if (!access) {
    return embedded ? <p style={note}>Checking access.</p> : <HeadquartersPage title="Books" subtitle="Checking access." />;
  }
  if (access.access_level !== "owner") {
    return embedded ? (
      <p style={note}>Only the workspace owner can open the books.</p>
    ) : (
      <HeadquartersPage title="Books" subtitle="Only the workspace owner can open the books." />
    );
  }

  const form = FORMS[key];
  const columns = (report?.columns || []).filter((column) => column.key !== "entry_id");
  const Shell = embedded ? "div" : HeadquartersPage;
  const shellProps = embedded
    ? { style: { marginTop: 12 } }
    : {
        title: "Books",
        subtitle: "The economist checklist, plus the QuickBooks reports these records can add up. Blank cells are missing, not zero.",
      };

  return (
    <Shell {...shellProps}>
      <style>{`
        .jt-books-caps { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
        .jt-books-cap, .jt-books-cap.on {
          border-radius: 999px; padding: 6px 12px; font-size: 13px; font-weight: 700; cursor: pointer;
        }
        .jt-books-cap { background: #10283d; color: #d7e7f5; border: 1px solid rgba(255,255,255,0.14); }
        .jt-books-cap.on { background: #f5c542; color: #062033; border-color: #f5c542; }
        .jt-books-table { width: 100%; border-collapse: collapse; color: #e8f1f8; font-size: 13px; }
        .jt-books-table th, .jt-books-table td { text-align: left; padding: 8px 6px; border-bottom: 1px solid rgba(255,255,255,0.08); vertical-align: top; }
        .jt-books-table th { color: #d7b24d; font-size: 11px; letter-spacing: 0.4px; text-transform: uppercase; }
        .jt-books-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 8px; }
        @media (max-width: 700px) { .jt-books-form { grid-template-columns: 1fr; } }
      `}</style>

      <div className="jt-books-caps">
        {REPORTS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={key === id ? "jt-books-cap on" : "jt-books-cap"}
            onClick={() => {
              setKey(id);
              setDraft({});
              setMsg("");
              load(id, days).catch((error) => setErr(error.message || "Could not load the report"));
            }}
          >
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
        <select
          value={days}
          onChange={(event) => {
            setDays(event.target.value);
            load(key, event.target.value).catch((error) => setErr(error.message || "Could not load the report"));
          }}
          style={field}
        >
          <option value="0">All dates</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
          <option value="365">Last 12 months</option>
        </select>
        <button type="button" onClick={() => report && downloadCsv(report)} disabled={!report?.rows?.length} style={goldButton}>
          Download CSV
        </button>
      </div>

      {err && <div style={errorBox}>{err}</div>}
      {report?.notes && <p style={note}>{report.notes}</p>}

      <div style={{ overflowX: "auto" }}>
        <table className="jt-books-table">
          <thead>
            <tr>
              {columns.map((column) => <th key={column.key}>{column.label}</th>)}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(report?.rows || []).map((row, index) => (
              <tr key={row.entry_id || row.id || index}>
                {columns.map((column) => <td key={column.key}>{row[column.key] === "" || row[column.key] == null ? "—" : String(row[column.key])}</td>)}
                <td>
                  {row.entry_id ? (
                    <button type="button" onClick={() => removeEntry(row.entry_id)} style={quietButton}>Remove</button>
                  ) : null}
                </td>
              </tr>
            ))}
            {report && report.rows.length === 0 && (
              <tr><td colSpan={columns.length + 1} style={{ color: "#9fb0c3" }}>No rows yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <form onSubmit={saveEntry} style={{ marginTop: 16 }}>
          <div style={kicker}>Add the numbers the app does not have</div>
          <div className="jt-books-form" style={{ marginTop: 8 }}>
            {form.fields.map(([name, label, type]) => (
              <label key={name} style={{ display: "block" }}>
                <div style={labelStyle}>{label}</div>
                <input
                  type={type}
                  value={draft[name] || ""}
                  onChange={(event) => setDraft({ ...draft, [name]: event.target.value })}
                  style={field}
                  required={name === "what" || name === "bill" || name === "week_of" || name === "ref_id" || name === "source"}
                />
              </label>
            ))}
          </div>
          <button type="submit" style={{ ...goldButton, marginTop: 10 }}>Save row</button>
          {msg && <span style={{ ...note, marginLeft: 10 }}>{msg}</span>}
        </form>
      )}
    </Shell>
  );
}

const field = {
  width: "100%",
  boxSizing: "border-box",
  background: "#0c2236",
  color: "#ffffff",
  border: "1px solid rgba(255,255,255,0.14)",
  borderRadius: 8,
  padding: "10px 12px",
  fontSize: 16,
};
const goldButton = {
  background: "#f5c542",
  color: "#062033",
  border: 0,
  borderRadius: 10,
  padding: "10px 14px",
  fontWeight: 900,
  cursor: "pointer",
};
const quietButton = {
  background: "transparent",
  color: "#ffb4b4",
  border: "1px solid rgba(255,180,180,0.45)",
  borderRadius: 8,
  padding: "6px 8px",
  cursor: "pointer",
};
const note = { color: "#9fb0c3", fontSize: 13, lineHeight: 1.45 };
const kicker = { color: "#d7b24d", fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase" };
const labelStyle = { color: "#9fb0c3", fontSize: 12, marginBottom: 4 };
const errorBox = { marginBottom: 12, padding: 12, borderRadius: 10, background: "rgba(255,80,80,0.12)", color: "#ffb4b4" };
